import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Save, Loader2, Check, Mail } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  CREDIT_TIERS, OHIO_COUNTIES, OHIO_TAX_PDF, OHIO_TAX_EFFECTIVE_DATE,
  FEE_DOC, FEE_STATE, FEE_TITLE, FEE_TEMP_TAG,
  PROTECTION_STANDARD, PROTECTION_DISCOUNT,
  computeOhioTax, computeMonthlyPayment, RATE_CEILING,
} from "../data/finance";
import { fmtMoney, siteConfig } from "../config";

const TERMS = [24, 36, 48, 60, 72, 84];

export default function PaymentCalculator({
  initialPrice = 28000,
  initialVehicleType = "new",   // "new" | "used"
  onChange,                     // optional callback({price, monthly, ...})
  testIdPrefix = "pmt",
  externalRebatesTotal = null,  // when provided, hides manual rebate input + uses this value
  rebatesPanel = null,          // v6.5.3 — React node rendered inline inside an expandable "Choose rebates" panel
  externalCoveragesTotal = null, // v6.5.2 — total of selected Optional Coverages, added to Amount Financed
  coveragesAnchorId = null,     // anchor id of the coverages selector (e.g. "byod-step-coverages")
  overrideApr = null,           // v6.5.8 — when set (e.g. Special APR offer selected), overrides credit-tier rate
  overrideTerm = null,          // v6.5.8 — when set, overrides customer's selected term (matches the APR special)
  overrideAprLabel = null,      // v6.5.8 — display label for the override notice
}) {
  // ----- Vehicle base -----
  const [price, setPrice] = useState(initialPrice);
  const [vehicleType, setVehicleType] = useState(initialVehicleType);
  const [down, setDown] = useState(2000);
  const [manualRebates, setManualRebates] = useState(0);
  // Effective rebates: external (from clickable rebate selector) overrides manual.
  const rebates = externalRebatesTotal !== null && externalRebatesTotal !== undefined
    ? Number(externalRebatesTotal) || 0
    : Number(manualRebates || 0);
  const usingExternalRebates = externalRebatesTotal !== null && externalRebatesTotal !== undefined;
  const setRebates = setManualRebates; // back-compat shim for any later refs
  const [term, setTerm] = useState(72);
  const [creditTier, setCreditTier] = useState("good");

  // ----- Optional trade -----
  const [includeTrade, setIncludeTrade] = useState(false);
  const [tradeValue, setTradeValue] = useState(0);
  const [tradePayoff, setTradePayoff] = useState(0);
  // v6.5.10 — Optional trade-context fields (lead-only, do not affect math)
  const [tradeYear, setTradeYear] = useState("");
  const [tradeMake, setTradeMake] = useState("");
  const [tradeModel, setTradeModel] = useState("");
  const [tradeMileage, setTradeMileage] = useState("");

  // ----- Tax (always on; Franklin default per Build My Own Deal spec) -----
  const includeTax = true;
  const [county, setCounty] = useState("Franklin");

  // ----- Optional Temporary Tag ($23) -----
  const [tempTag, setTempTag] = useState(false);

  // v6.5.3 — Inline "Choose rebates" expandable panel (replaces removed Step 4b)
  const [showRebatesPanel, setShowRebatesPanel] = useState(false);

  // Sync price when the parent passes a real vehicle price after async load.
  useEffect(() => {
    if (initialPrice !== undefined && initialPrice !== null && Number(initialPrice) > 0) {
      setPrice(initialPrice);
    }
  }, [initialPrice]);

  // ----- Paint & Fabric protection (with smart discount retry) -----
  // protectionStage: "asking" → "discount-offered" → "declined" | "accepted-standard" | "accepted-discount"
  const [protectionStage, setProtectionStage] = useState("asking");

  const declineProtection = () => {
    if (protectionStage === "asking") setProtectionStage("discount-offered");
    else setProtectionStage("declined");
  };
  const acceptProtection = (which) => {
    setProtectionStage(which === "discount" ? "accepted-discount" : "accepted-standard");
  };

  const protectionAmount =
    protectionStage === "accepted-standard" ? PROTECTION_STANDARD :
    protectionStage === "accepted-discount" ? PROTECTION_DISCOUNT : 0;

  // ----- Calculations -----
  const calc = useMemo(() => {
    const countyRow = OHIO_COUNTIES.find((c) => c.name === county) || OHIO_COUNTIES[0];
    const taxRate = countyRow.rate;

    const effectiveTradeValue = includeTrade ? Number(tradeValue || 0) : 0;
    const effectivePayoff = includeTrade ? Number(tradePayoff || 0) : 0;
    const netTrade = effectiveTradeValue - effectivePayoff;       // negative if upside-down
    // v6.5.10 — Surface equity / negative equity separately for the UI summary
    const equity = effectiveTradeValue - effectivePayoff;          // > 0 = positive equity
    const negativeEquity = equity < 0 ? Math.abs(equity) : 0;
    // Eligible Trade Allowance is the trade VALUE (not net) per Ohio rules —
    // payoff doesn't reduce the taxable-credit. Only the appraised trade
    // value is credited against the taxable base.
    const taxableAmount = Math.max(0, Number(price || 0) - effectiveTradeValue);
    const tax = includeTax
      ? computeOhioTax({
          price: Number(price || 0),
          tradeValue: effectiveTradeValue,
          rebates: Number(rebates || 0),
          rate: taxRate,
        })
      : 0;

    const tier = CREDIT_TIERS.find((t) => t.value === creditTier) || CREDIT_TIERS[1];
    // v6.5.8 — Special APR / term override (when customer picks a special-financing offer).
    const hasOverrideApr = overrideApr !== null && overrideApr !== undefined && Number(overrideApr) >= 0;
    const hasOverrideTerm = overrideTerm !== null && overrideTerm !== undefined && Number(overrideTerm) > 0;
    const annualRate = hasOverrideApr
      ? Number(overrideApr) / 100
      : (vehicleType === "new" ? tier.new : tier.used);
    const effectiveTerm = hasOverrideTerm ? Number(overrideTerm) : term;

    // amount financed = price + tax + fees + protection − down − net trade − rebates
    // Rebates reduce cash out the door (they're applied like manufacturer money).
    // v6.5.1 — Fees per Ohio spec: Doc + State + Title (+ optional Temp Tag).
    // v6.5.2 — Optional Coverages (GAP, VSC, Paint & Fabric, etc.) added on top.
    const docFee = FEE_DOC;
    const stateFee = FEE_STATE;
    const titleFee = FEE_TITLE;
    const tempTagFee = tempTag ? FEE_TEMP_TAG : 0;
    const fees = docFee + stateFee + titleFee + tempTagFee;
    const coveragesTotal = externalCoveragesTotal !== null && externalCoveragesTotal !== undefined
      ? Math.max(0, Number(externalCoveragesTotal) || 0)
      : 0;
    const amountFinanced = Math.max(
      0,
      Number(price || 0)
        + tax
        + fees
        + protectionAmount
        + coveragesTotal
        - Number(down || 0)
        - netTrade
        - Number(rebates || 0),
    );

    const monthly = computeMonthlyPayment({ amountFinanced, annualRate, months: effectiveTerm });

    return {
      taxRate, tax, fees, docFee, stateFee, titleFee, tempTagFee, netTrade, amountFinanced, monthly,
      countyName: countyRow.name,
      protectionAmount,
      coveragesTotal,
      annualRate,
      effectiveTerm,
      usingOverrideApr: hasOverrideApr,
      usingOverrideTerm: hasOverrideTerm,
      // v6.5.10 — trade details surfaced to UI summary + lead payload
      equity,
      negativeEquity,
      taxableAmount,
      effectiveTradeValue,
      effectivePayoff,
    };
  }, [
    price, vehicleType, down, rebates, term, creditTier,
    includeTrade, tradeValue, tradePayoff,
    includeTax, county, protectionAmount, tempTag, externalCoveragesTotal,
    overrideApr, overrideTerm,
    tradeYear, tradeMake, tradeModel, tradeMileage,
  ]);

  // Bubble up to parent (BYOD submit etc.) — must be useEffect so the parent
  // setState is queued AFTER this render commits, not during it.
  useEffect(() => {
    if (onChange) onChange({
      price: Number(price || 0),
      vehicle_type: vehicleType,
      down: Number(down || 0),
      term, credit_tier: creditTier,
      effective_term: calc.effectiveTerm,
      using_override_apr: calc.usingOverrideApr,
      using_override_term: calc.usingOverrideTerm,
      override_apr: calc.usingOverrideApr ? Number(overrideApr) : null,
      override_apr_label: calc.usingOverrideApr ? (overrideAprLabel || null) : null,
      include_trade: includeTrade, trade_value: tradeValue, trade_payoff: tradePayoff,
      // v6.5.10 — full trade lead context
      trade_year: includeTrade ? tradeYear : "",
      trade_make: includeTrade ? tradeMake : "",
      trade_model: includeTrade ? tradeModel : "",
      trade_mileage: includeTrade ? tradeMileage : "",
      trade_equity: includeTrade ? calc.equity : 0,
      trade_negative_equity: includeTrade ? calc.negativeEquity : 0,
      taxable_amount: calc.taxableAmount,
      include_tax: includeTax, county: calc.countyName, tax: calc.tax,
      rebates: Number(rebates || 0),
      protection: calc.protectionAmount,
      coverages_total: calc.coveragesTotal,
      doc_fee: calc.docFee,
      state_fee: calc.stateFee,
      title_fee: calc.titleFee,
      temp_tag_fee: calc.tempTagFee,
      temp_tag_selected: tempTag,
      // Back-compat: total of non-doc fees rolled into gov_fee for older lead handlers.
      gov_fee: calc.stateFee + calc.titleFee + calc.tempTagFee,
      amount_financed: calc.amountFinanced,
      monthly: calc.monthly,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calc.monthly, calc.amountFinanced]);

  return (
    <div className="ssc-card p-4 sm:p-5" data-testid={`${testIdPrefix}-calc`}>
      <div className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-[var(--ssc-blue)]">Your Deal Numbers</div>
      <h3 className="text-2xl font-black text-[var(--ssc-navy)] mt-1">Your Deal Numbers</h3>
      <p className="text-sm text-[var(--ssc-ink-2)] mt-1">
        Real pricing pulled from this vehicle — adjust the inputs below to see how your monthly payment changes. Trade info is optional.
      </p>

      {/* Vehicle basics */}
      <Fieldset legend="Vehicle">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Vehicle Price *">
            <input inputMode="numeric" className="ssc-input" value={price} onChange={(e) => setPrice(e.target.value)} data-testid={`${testIdPrefix}-price`} />
          </Field>
          <Field label="Vehicle Type *">
            <div className="flex gap-2">
              {["new", "used"].map((t) => (
                <button
                  key={t} type="button"
                  className={`flex-1 ssc-btn text-sm ${vehicleType === t ? "ssc-btn-primary" : "ssc-btn-secondary"}`}
                  onClick={() => setVehicleType(t)}
                  data-testid={`${testIdPrefix}-vtype-${t}`}
                >{t === "new" ? "New" : "Used"}</button>
              ))}
            </div>
          </Field>
          <Field label="Down Payment">
            <input inputMode="numeric" className="ssc-input" value={down} onChange={(e) => setDown(e.target.value)} data-testid={`${testIdPrefix}-down`} />
          </Field>
          <Field label="Rebates / Manufacturer Cash">
            {usingExternalRebates ? (
              <div className="ssc-input flex items-center justify-between gap-2 bg-blue-50 border-[var(--ssc-blue)]" data-testid={`${testIdPrefix}-rebates-external`}>
                <span className="font-bold text-[var(--ssc-navy)] tabular-nums">{fmtMoney(rebates)}</span>
                {rebatesPanel ? (
                  <button
                    type="button"
                    onClick={() => setShowRebatesPanel((v) => !v)}
                    className="text-xs font-bold text-[var(--ssc-blue)] hover:underline"
                    aria-expanded={showRebatesPanel}
                    data-testid={`${testIdPrefix}-toggle-rebates-panel`}
                  >
                    {showRebatesPanel ? "Hide rebates ↑" : "Choose rebates ↓"}
                  </button>
                ) : (
                  <span className="text-[10px] uppercase tracking-wide font-bold text-[var(--ssc-blue)]">From selected rebates</span>
                )}
              </div>
            ) : (
              <input inputMode="numeric" className="ssc-input" value={manualRebates} onChange={(e) => setManualRebates(e.target.value)} data-testid={`${testIdPrefix}-rebates`} />
            )}
          </Field>
          <Field label="Term">
            <select
              className={`ssc-select ${calc.usingOverrideTerm ? "opacity-60 cursor-not-allowed" : ""}`}
              value={calc.usingOverrideTerm ? Number(overrideTerm) : term}
              onChange={(e) => setTerm(Number(e.target.value))}
              disabled={calc.usingOverrideTerm}
              data-testid={`${testIdPrefix}-term`}
            >
              {(calc.usingOverrideTerm && !TERMS.includes(Number(overrideTerm))) && (
                <option value={Number(overrideTerm)}>{Number(overrideTerm)} months</option>
              )}
              {TERMS.map((m) => <option key={m} value={m}>{m} months</option>)}
            </select>
          </Field>
          <Field label="Your Credit (estimate)">
            <select
              className={`ssc-select ${calc.usingOverrideApr ? "opacity-60 cursor-not-allowed" : ""}`}
              value={creditTier}
              onChange={(e) => setCreditTier(e.target.value)}
              disabled={calc.usingOverrideApr}
              data-testid={`${testIdPrefix}-credit`}
            >
              {CREDIT_TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
        </div>

        {/* v6.5.8 — Special APR / term override banner */}
        {(calc.usingOverrideApr || calc.usingOverrideTerm) && (
          <div
            className="mt-3 rounded-xl border-2 border-emerald-500 bg-emerald-50 px-4 py-3 text-sm"
            data-testid={`${testIdPrefix}-special-apr-banner`}
          >
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-emerald-700">
              Special Financing Active
            </div>
            <div className="mt-1 text-[var(--ssc-navy)] font-bold">
              {overrideAprLabel || `${(Number(overrideApr) || 0).toFixed(2)}% APR for ${overrideTerm} months`}
            </div>
            <p className="mt-1 text-[11px] text-[var(--ssc-ink-2)] leading-snug">
              Your payment below is calculated using this special rate and term. Credit approval required —
              your actual rate and approval will be confirmed by Byers Chevrolet. To switch back to your selected
              credit tier and term, deselect the special-financing offer in <strong>Choose rebates</strong>.
            </p>
          </div>
        )}

        {/* v6.5.3 — inline expandable rebates panel (replaces removed Step 4b). */}
        {rebatesPanel && showRebatesPanel && (
          <div
            id="byod-rebates-panel"
            className="mt-4 rounded-xl border border-[var(--ssc-blue)]/30 bg-[var(--ssc-blue)]/5 p-4"
            data-testid={`${testIdPrefix}-rebates-panel`}
          >
            <p className="text-xs text-[var(--ssc-ink-2)] mb-3">
              <span className="font-bold text-emerald-700">Public rebates</span> are
              available to everyone when program rules are met.{" "}
              <span className="font-bold text-amber-700">Conditional offers</span>{" "}
              may require proof, eligibility, financing, ownership, membership, or other
              qualifications. The site enforces GM stacking rules so you can&apos;t accidentally
              pick offers that don&apos;t combine.
            </p>
            {rebatesPanel}
          </div>
        )}
      </Fieldset>

      {/* Optional trade */}
      <Fieldset
        legend="Trade-In (optional)"
        toggle={{
          checked: includeTrade,
          onChange: (v) => setIncludeTrade(v),
          testId: `${testIdPrefix}-include-trade`,
        }}
      >
        {includeTrade ? (
          <div className="space-y-3">
            {/* Required for math */}
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Trade-in value">
                <input inputMode="numeric" className="ssc-input" value={tradeValue} onChange={(e) => setTradeValue(e.target.value)} data-testid={`${testIdPrefix}-tradeval`} placeholder="0" />
              </Field>
              <Field label="Trade-in payoff (loan balance)">
                <input inputMode="numeric" className="ssc-input" value={tradePayoff} onChange={(e) => setTradePayoff(e.target.value)} data-testid={`${testIdPrefix}-tradepayoff`} placeholder="0" />
              </Field>
            </div>

            {/* Trade context (optional — lead-only, no math impact) */}
            <div className="rounded-xl border border-[var(--ssc-line)] bg-[var(--ssc-blue)]/[0.03] p-3">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-ink-2)] mb-2">
                Trade Context (optional · helps Scott price your trade accurately)
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                <Field label="Year">
                  <input inputMode="numeric" className="ssc-input" value={tradeYear} onChange={(e) => setTradeYear(e.target.value)} data-testid={`${testIdPrefix}-trade-year`} placeholder="2020" />
                </Field>
                <Field label="Make">
                  <input className="ssc-input" value={tradeMake} onChange={(e) => setTradeMake(e.target.value)} data-testid={`${testIdPrefix}-trade-make`} placeholder="Chevrolet" />
                </Field>
                <Field label="Model">
                  <input className="ssc-input" value={tradeModel} onChange={(e) => setTradeModel(e.target.value)} data-testid={`${testIdPrefix}-trade-model`} placeholder="Equinox" />
                </Field>
                <Field label="Mileage">
                  <input inputMode="numeric" className="ssc-input" value={tradeMileage} onChange={(e) => setTradeMileage(e.target.value)} data-testid={`${testIdPrefix}-trade-mileage`} placeholder="58,000" />
                </Field>
              </div>
              <a
                href="/value-my-trade"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-3 text-xs font-extrabold text-[var(--ssc-blue)] hover:underline"
                data-testid={`${testIdPrefix}-appraisal-cta`}
              >
                Get my trade appraised →
              </a>
            </div>

            {/* Live trade summary */}
            {(Number(tradeValue) > 0 || Number(tradePayoff) > 0) && (
              <div className="rounded-xl border border-[var(--ssc-line)] bg-white p-3 text-sm" data-testid={`${testIdPrefix}-trade-summary`}>
                <div className="flex justify-between"><span className="text-[var(--ssc-ink-2)]">Trade Value</span><span className="font-bold text-[var(--ssc-navy)] tabular-nums">{fmtMoney(calc.effectiveTradeValue)}</span></div>
                <div className="flex justify-between mt-1"><span className="text-[var(--ssc-ink-2)]">Payoff</span><span className="font-bold text-[var(--ssc-navy)] tabular-nums">{fmtMoney(calc.effectivePayoff)}</span></div>
                <div className="flex justify-between mt-1 pt-2 border-t border-[var(--ssc-line)]">
                  {calc.equity >= 0 ? (
                    <>
                      <span className="text-[var(--ssc-ink)] font-bold">Estimated Equity</span>
                      <span className="font-extrabold text-emerald-700 tabular-nums" data-testid={`${testIdPrefix}-trade-equity`}>{fmtMoney(calc.equity)}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-[var(--ssc-ink)] font-bold">Estimated Negative Equity</span>
                      <span className="font-extrabold text-amber-700 tabular-nums" data-testid={`${testIdPrefix}-trade-negative-equity`}>{fmtMoney(calc.negativeEquity)}</span>
                    </>
                  )}
                </div>
                <p className="text-[11px] text-[var(--ssc-ink-2)] mt-2 leading-snug">
                  {calc.equity >= 0
                    ? "Positive equity reduces your amount financed."
                    : "Negative equity (payoff higher than trade value) is added to your amount financed."}
                </p>
              </div>
            )}

            {/* Ohio taxable-amount breakdown */}
            {Number(tradeValue) > 0 && (
              <div className="rounded-lg bg-[var(--ssc-blue)]/[0.06] border border-[var(--ssc-blue)]/20 px-3 py-2.5 text-xs" data-testid={`${testIdPrefix}-trade-taxable`}>
                <div className="font-extrabold uppercase tracking-widest text-[var(--ssc-blue)] text-[10px]">Ohio Taxable Amount</div>
                <div className="mt-1 flex flex-wrap items-baseline gap-x-2 tabular-nums">
                  <span>{fmtMoney(price)}</span>
                  <span className="text-[var(--ssc-ink-2)]">−</span>
                  <span>{fmtMoney(calc.effectiveTradeValue)} trade allowance</span>
                  <span className="text-[var(--ssc-ink-2)]">=</span>
                  <span className="font-extrabold text-[var(--ssc-navy)]">{fmtMoney(calc.taxableAmount)} estimated taxable amount</span>
                </div>
                <p className="text-[11px] text-[var(--ssc-ink-2)] mt-1 leading-snug">
                  {vehicleType === "new"
                    ? "Ohio credits your trade allowance against the taxable base on new vehicles. Rebates and cash down do not reduce tax."
                    : "Ohio credits your trade allowance against the taxable base on used vehicles too."}
                </p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-[var(--ssc-ink-2)]">No trade — flip the toggle on the right if you want to include one.</p>
        )}
      </Fieldset>

      {/* Sales tax — always on (Ohio Deal Desk). Default county: Franklin. */}
      <Fieldset legend="Sales Tax (Ohio)">
        <div>
          <Field label="Your county (Ohio)">
            <select className="ssc-select" value={county} onChange={(e) => setCounty(e.target.value)} data-testid={`${testIdPrefix}-county`}>
              {OHIO_COUNTIES.map((c) => <option key={c.name} value={c.name}>{`${c.name} — ${(c.rate * 100).toFixed(2)}%`}</option>)}
            </select>
          </Field>
          <p className="text-xs text-[var(--ssc-ink-2)] mt-2">
            Estimated using <strong>{(calc.taxRate * 100).toFixed(2)}%</strong> for {calc.countyName} County. Rates pulled from the Ohio Department of Taxation map (effective {OHIO_TAX_EFFECTIVE_DATE}). Sales tax is a guesstimate only — official rates change and may include local additions. Check the{" "}
            <a href={OHIO_TAX_PDF} target="_blank" rel="noopener noreferrer" className="text-[var(--ssc-blue)] font-bold underline" data-testid={`${testIdPrefix}-tax-pdf`}>
              Ohio Department of Taxation sales-tax map
            </a> for the current rate.
            {" "}Ohio rule: only an eligible trade allowance reduces the taxable amount — <strong>rebates and cash down are taxable</strong>.
          </p>
        </div>
      </Fieldset>

      {/* Fees & optional Temp Tag */}
      <Fieldset legend="Dealer & State Fees">
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <FeeRow label="Doc Fee" value={fmtMoney(calc.docFee)} />
          <FeeRow label="State Fee" value={fmtMoney(calc.stateFee)} />
          <FeeRow label="Title Fee" value={fmtMoney(calc.titleFee)} />
          <label className="flex items-center justify-between gap-2 rounded-lg border border-[var(--ssc-line)] bg-white px-3 py-2 cursor-pointer" data-testid={`${testIdPrefix}-temptag-wrap`}>
            <span className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={tempTag}
                onChange={(e) => setTempTag(e.target.checked)}
                className="w-4 h-4"
                data-testid={`${testIdPrefix}-temptag`}
              />
              <span className="font-semibold text-[var(--ssc-ink)]">Temporary Tag (optional)</span>
            </span>
            <span className={`font-extrabold tabular-nums ${tempTag ? "text-[var(--ssc-navy)]" : "text-[var(--ssc-ink-2)]"}`}>{fmtMoney(FEE_TEMP_TAG)}</span>
          </label>
        </div>
      </Fieldset>

      {/* Paint & Fabric protection (hidden when external coverages selector is wired,
          so Paint & Fabric Defense in the Coverages step never double-counts). */}
      {externalCoveragesTotal === null && (
      <Fieldset legend="Paint & Fabric Protection">
        {protectionStage === "asking" && (
          <div className="space-y-2" data-testid={`${testIdPrefix}-prot-asking`}>
            <p className="text-sm text-[var(--ssc-ink)]">
              Want to add Paint & Fabric Protection? Standard price <strong>{fmtMoney(PROTECTION_STANDARD)}</strong>.
            </p>
            <div className="flex gap-2">
              <button type="button" onClick={() => acceptProtection("standard")} className="ssc-btn ssc-btn-primary text-sm" data-testid={`${testIdPrefix}-prot-accept`}>
                Yes, add it ({fmtMoney(PROTECTION_STANDARD)})
              </button>
              <button type="button" onClick={declineProtection} className="ssc-btn ssc-btn-secondary text-sm" data-testid={`${testIdPrefix}-prot-decline-1`}>
                No thanks
              </button>
            </div>
          </div>
        )}
        {protectionStage === "discount-offered" && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2" data-testid={`${testIdPrefix}-prot-discount`}>
            <p className="text-sm text-[var(--ssc-ink)]">
              <strong>One-time discount:</strong> add Paint & Fabric Protection for <strong>{fmtMoney(PROTECTION_DISCOUNT)}</strong> instead of {fmtMoney(PROTECTION_STANDARD)}.
            </p>
            <div className="flex gap-2">
              <button type="button" onClick={() => acceptProtection("discount")} className="ssc-btn ssc-btn-primary text-sm" data-testid={`${testIdPrefix}-prot-accept-discount`}>
                Yes — add at {fmtMoney(PROTECTION_DISCOUNT)}
              </button>
              <button type="button" onClick={declineProtection} className="ssc-btn ssc-btn-secondary text-sm" data-testid={`${testIdPrefix}-prot-decline-2`}>
                No thanks
              </button>
            </div>
          </div>
        )}
        {protectionStage === "declined" && (
          <p className="text-xs text-[var(--ssc-ink-2)]" data-testid={`${testIdPrefix}-prot-declined`}>
            Skipped — Paint & Fabric Protection not included.
          </p>
        )}
        {(protectionStage === "accepted-standard" || protectionStage === "accepted-discount") && (
          <div className="flex justify-between items-center text-sm" data-testid={`${testIdPrefix}-prot-accepted`}>
            <span className="text-[var(--ssc-ink)] font-semibold">Paint & Fabric Protection added</span>
            <span className="font-extrabold text-[var(--ssc-blue)]">{fmtMoney(protectionAmount)}</span>
          </div>
        )}
      </Fieldset>
      )}

      {/* Result */}
      <div className="mt-5 bg-[var(--ssc-paper)] rounded-2xl p-5 border border-[var(--ssc-line)]" data-testid={`${testIdPrefix}-result`}>
        <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-ink-2)]">Estimated Monthly Payment</div>
        <div className="text-4xl font-black text-[var(--ssc-navy)] mt-1" data-testid={`${testIdPrefix}-monthly`}>
          {fmtMoney(calc.monthly)}<span className="text-base font-bold text-[var(--ssc-ink-2)]">/mo for {term} mo</span>
        </div>

        <div className="grid sm:grid-cols-2 gap-2 mt-4 text-sm">
          <Line label="Vehicle price" value={fmtMoney(price)} />
          {includeTrade && <Line label="Trade-in" value={`− ${fmtMoney(calc.netTrade)}`} />}
          <Line label="Rebates" value={`− ${fmtMoney(rebates)}`} />
          <Line label="Down payment" value={`− ${fmtMoney(down)}`} />
          {includeTax && <Line label={`Sales tax (${calc.countyName})`} value={`+ ${fmtMoney(calc.tax)}`} />}
          <Line label="Doc fee" value={`+ ${fmtMoney(calc.docFee)}`} />
          <Line label="State fee" value={`+ ${fmtMoney(calc.stateFee)}`} />
          <Line label="Title fee" value={`+ ${fmtMoney(calc.titleFee)}`} />
          {tempTag && <Line label="Temporary tag" value={`+ ${fmtMoney(calc.tempTagFee)}`} />}
          {protectionAmount > 0 && <Line label="Paint & Fabric Protection" value={`+ ${fmtMoney(protectionAmount)}`} />}
          {calc.coveragesTotal > 0 && (
            <Line
              label={coveragesAnchorId ? "Optional coverages" : "Optional coverages"}
              value={`+ ${fmtMoney(calc.coveragesTotal)}`}
            />
          )}
          <Line label="Amount financed" value={fmtMoney(calc.amountFinanced)} highlight />
        </div>

        <p className="text-xs text-[var(--ssc-ink-2)] mt-4 leading-relaxed">
          <strong>Not an actual rate quote.</strong> Monthly payment is a guesstimate based on national-average rates for the credit tier you selected. Real APR depends on full credit underwriting, lender, term, and the vehicle — your offer could be higher or lower. Maximum rate used in this calculator is <strong>{(RATE_CEILING * 100).toFixed(2)}%</strong>. Text Scott at <a className="text-[var(--ssc-blue)] font-bold" href={siteConfig.scott.phoneSms}>{siteConfig.scott.phone}</a> for a real number from his lenders.
        </p>

        <SavePaymentActions calc={calc} price={price} vehicleType={vehicleType} down={down}
                            rebates={rebates} term={term} creditTier={creditTier}
                            includeTrade={includeTrade} tradeValue={tradeValue} tradePayoff={tradePayoff}
                            includeTax={includeTax} testIdPrefix={testIdPrefix} />
      </div>
    </div>
  );
}

function SavePaymentActions({ calc, price, vehicleType, down, rebates, term, creditTier,
                              includeTrade, tradeValue, tradePayoff, includeTax, testIdPrefix }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [savedCode, setSavedCode] = useState(null);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/deals", {
        deal_type: "byod",
        vehicle: { price: Number(price), vehicle_type: vehicleType },
        payment: {
          monthly: Math.round(calc.monthly),
          amount_financed: Math.round(calc.amountFinanced),
          term, credit_tier: creditTier,
          down: Number(down), rebates: Number(rebates),
          include_trade: includeTrade,
          trade_value: includeTrade ? Number(tradeValue) : 0,
          trade_payoff: includeTrade ? Number(tradePayoff) : 0,
          include_tax: includeTax,
          tax: Math.round(calc.tax),
          county: calc.countyName,
          tax_rate: calc.taxRate,
          doc_fee: calc.docFee,
          state_fee: calc.stateFee,
          title_fee: calc.titleFee,
          temp_tag_fee: calc.tempTagFee,
          gov_fee: calc.stateFee + calc.titleFee + calc.tempTagFee,
          protection: calc.protectionAmount,
        },
        title: `Payment quote · ${fmtMoney(price)} ${vehicleType} · ${fmtMoney(calc.monthly)}/mo for ${term} mo`,
      });
      setSavedCode(data.deal.code);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save right now.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-[var(--ssc-line)] flex flex-wrap gap-2 items-center" data-testid={`${testIdPrefix}-save-actions`}>
      {user ? (
        <button onClick={save} disabled={busy} className="ssc-btn ssc-btn-primary text-sm inline-flex items-center gap-2" data-testid={`${testIdPrefix}-save`}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Payment to My Account
        </button>
      ) : (
        <Link to="/signup" className="ssc-btn ssc-btn-primary text-sm inline-flex items-center gap-2" data-testid={`${testIdPrefix}-save-signup`}>
          <Save size={15} /> Sign Up to Save This Payment
        </Link>
      )}
      <Link to="/contact" className="ssc-btn ssc-btn-secondary text-sm inline-flex items-center gap-2" data-testid={`${testIdPrefix}-email`}>
        <Mail size={15} /> Email This to Scott
      </Link>

      {savedCode && (
        <div className="w-full mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-900 inline-flex items-center gap-2" data-testid={`${testIdPrefix}-saved-msg`}>
          <Check size={15} /> Saved as <span className="font-mono font-bold text-[var(--ssc-navy)]">{savedCode}</span> — share this code with Scott to pull it up instantly.
        </div>
      )}
      {err && <div className="w-full mt-2 text-sm text-red-600">{err}</div>}
    </div>
  );
}

const Fieldset = ({ legend, children, toggle }) => (
  <fieldset className="border border-[var(--ssc-line)] rounded-xl sm:rounded-2xl p-3 sm:p-4 mt-3 sm:mt-4">
    <legend className="flex items-center gap-3 text-xs font-extrabold tracking-widest uppercase text-[var(--ssc-blue)] px-2">
      <span>{legend}</span>
      {toggle && (
        <label className="inline-flex items-center gap-2 normal-case tracking-normal cursor-pointer">
          <input
            type="checkbox"
            checked={toggle.checked}
            onChange={(e) => toggle.onChange(e.target.checked)}
            data-testid={toggle.testId}
            className="w-4 h-4"
          />
          <span className="text-[var(--ssc-ink-2)] text-xs font-bold">Include</span>
        </label>
      )}
    </legend>
    {children}
  </fieldset>
);

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="ssc-label">{label}</label>
    {children}
  </div>
);

const Line = ({ label, value, highlight }) => (
  <div className={`flex justify-between items-center ${highlight ? "pt-2 border-t border-[var(--ssc-line)] font-extrabold text-[var(--ssc-navy)]" : "text-[var(--ssc-ink)]"}`}>
    <span className="text-[var(--ssc-ink-2)]">{label}</span>
    <span>{value}</span>
  </div>
);

const FeeRow = ({ label, value }) => (
  <div className="flex items-center justify-between rounded-lg border border-[var(--ssc-line)] bg-white px-3 py-2">
    <span className="font-semibold text-[var(--ssc-ink)]">{label}</span>
    <span className="font-extrabold text-[var(--ssc-navy)] tabular-nums">{value}</span>
  </div>
);
